import type { Millimes } from '@beta-assur/shared';
import { Link } from '@tanstack/react-router';
import { ConfirmButton } from '../components/confirm';
import { Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TBody, TD, TR } from '../components/ui/table';
import { useApi } from '../lib/auth';
import { instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import { useTenant } from '../lib/referentials';

// Prélèvement automatique (BA-072) : mandat signé par l'assuré depuis sa page, derniers
// prélèvements ; le gestionnaire peut l'arrêter à la demande de l'assuré.

type MandateStatus = 'pending' | 'active' | 'cancelled' | 'failed';
type ChargeStatus = 'pending' | 'succeeded' | 'failed';

interface MandateView {
  mandate: {
    id: string;
    status: MandateStatus;
    maxAmount: Millimes;
    activatedAt: string | null;
  } | null;
  charges: {
    id: string;
    installmentId: string;
    amount: Millimes;
    status: ChargeStatus;
    reason: string | null;
    createdAt: string;
  }[];
}

const MANDATE_TONE = {
  pending: 'info',
  active: 'success',
  cancelled: 'neutral',
  failed: 'danger',
} as const;
const CHARGE_TONE = { pending: 'info', succeeded: 'success', failed: 'danger' } as const;

export function MandateCard({ contractId, editable }: { contractId: string; editable: boolean }) {
  const { t, language } = useI18n();
  const api = useApi();
  const tz = useTenant().data?.timezone;
  const query = useApiQuery<MandateView>(`/v1/contracts/${contractId}/mandate`);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.mandate.title}</CardTitle>
        {query.data?.mandate ? (
          <Badge tone={MANDATE_TONE[query.data.mandate.status]}>
            {t.mandate.statuses[query.data.mandate.status]}
          </Badge>
        ) : null}
      </CardHeader>
      <QueryView query={query}>
        {(data) =>
          !data.mandate ? (
            <Empty>{t.mandate.none}</Empty>
          ) : (
            <>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <span>
                  {t.mandate.cap} : {money(data.mandate.maxAmount, language)}
                  {data.mandate.activatedAt
                    ? ` · ${t.mandate.since} ${instant(data.mandate.activatedAt, tz, language)}`
                    : ''}
                </span>
                {editable &&
                (data.mandate.status === 'active' || data.mandate.status === 'pending') ? (
                  <ConfirmButton
                    label={t.mandate.cancel}
                    title={t.mandate.cancel}
                    message={t.mandate.cancelMessage}
                    variant="destructive"
                    onConfirm={async () => {
                      await api.request('POST', `/v1/contracts/${contractId}/mandate/cancel`);
                      await query.refetch();
                    }}
                  />
                ) : null}
              </CardContent>
              {data.charges.length ? (
                <Table>
                  <TBody>
                    {data.charges.map((ch) => (
                      <TR key={ch.id}>
                        <TD className="whitespace-nowrap">{instant(ch.createdAt, tz, language)}</TD>
                        <TD>
                          <Link
                            to="/echeances/$id"
                            params={{ id: ch.installmentId }}
                            className="underline"
                          >
                            {t.mandate.installment}
                          </Link>
                        </TD>
                        <TD className="tabular-nums">{money(ch.amount, language)}</TD>
                        <TD>
                          <Badge tone={CHARGE_TONE[ch.status]}>
                            {t.mandate.chargeStatuses[ch.status]}
                          </Badge>
                          {ch.reason ? (
                            <span className="ms-2 text-xs text-muted-foreground">{ch.reason}</span>
                          ) : null}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              ) : null}
            </>
          )
        }
      </QueryView>
    </Card>
  );
}
